import type { UseFetchOptions } from '#app';
import CookieNameSpace from '~/const/cookie';
import { ERRORS_TRANSLATE } from '~/const/errors';
import { AlertsTypes, TIMEDEAD_DEFAULT_FOR_ALERT } from '~/const/notification';
import { useBaseStore } from '~/store/base.store';
import { useNotificationStore } from '~/store/notification.store';
import { useProfileStore } from '~/store/profile.store';
import { getConnectionStatus } from '~/helpers/network';
import { enqueueOfflineRequest, getCachedGet, getGetCacheIdentity, saveCachedGet } from '~/helpers/offlineRequestQueue';

export const useCustomFetch = async <T>(request: string, opts: UseFetchOptions<T>, customOpts?: CustomOpts) => {
	const requestPath = useRuntimeConfig().public.isMobile ? useRuntimeConfig().public.baseURL + request : request;
	const method = String(opts.method || 'GET').toUpperCase();
	const isGetRequest = method === 'GET';
	const isNeedOffline = customOpts?.isNeedOffline !== false;
	const isOnline = process.client ? await getConnectionStatus() : true;
	const isQueueableRequest =
		['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !/\/api\/auth(?:\/|$)/i.test(requestPath);
	const baseStore = useBaseStore();
	const { getAuthToken } = useAuthToken();
	const suppliedToken = new Headers(opts.headers as HeadersInit | undefined).get('token');
	const token = suppliedToken || (await getAuthToken());

	const headers = Object.fromEntries(new Headers(opts.headers as HeadersInit | undefined).entries());
	if (token) {
		headers.token = token;
		headers.authorization = `Bearer ${token}`;
	}
	opts = { ...opts, headers };

	const { onResponse: customOnResponse, ...fetchOptions } = opts;
	const cacheIdentity =
		process.client && isGetRequest && isNeedOffline ? await getGetCacheIdentity(request, opts.query, token) : null;
	let cachedGet = null;
	if (cacheIdentity) {
		try {
			cachedGet = await getCachedGet(cacheIdentity.key);
		} catch (error) {
			console.error('Failed to read cached GET response:', error);
		}
	}

	const cachedResponse = async () => {
		let response = cachedGet?.response as T;
		if (opts.transform) {
			response = await opts.transform(response);
		}
		return {
			data: ref<T | null>(response),
			error: ref(null),
			status: ref<'success'>('success'),
			pending: ref(false),
			offlineRequestId: undefined
		};
	};

	if (isGetRequest && !isOnline) {
		if (cachedGet) return cachedResponse();
		return {
			data: ref<T | null>(null),
			error: ref(new Error(`No cached response is available while offline: ${request}`)),
			status: ref<'error'>('error'),
			pending: ref(false),
			offlineRequestId: undefined
		};
	}

	if (process.client && isQueueableRequest && !customOpts?.skipOfflineQueue && !isOnline) {
		const offlineRequestId = await enqueueOfflineRequest(request, opts);
		customOpts?.setLoading?.(false);
		useNotificationStore().addNewAlert(
			'Нет подключения. Изменение сохранено и отправится автоматически после восстановления сети.',
			5000,
			AlertsTypes.WARNING
		);
		return {
			data: ref<T | null>(null),
			error: ref(null),
			status: ref<'pending'>('pending'),
			offlineRequestId
		};
	}

	const now = Date.now();
	let requestQueued = false;
	let requestFailedBeforeResponse = false;
	let offlineRequestId: number | undefined;

	const result = await useFetch(requestPath, {
		key: requestPath + '-' + now,
		timeout: opts.timeout ?? (method === 'GET' ? 8000 : opts.body instanceof FormData ? 120000 : 20000),
		onRequest: async (res) => {
			if (!customOpts?.isSilent) baseStore.isLoading = true;
			CustomOptsUseage(customOpts, TypeEvents.onRequest);
		},
		onResponse: async (res) => {
			if (res.response.ok && !customOpts?.isDisabledShowMsgSuccess && res.response._data?.msg) {
				const notification = useNotificationStore();
				notification.addNewAlert(res.response._data?.msg, 4000, AlertsTypes.SUCCESS);
			}

			if (cacheIdentity && res.response.ok && res.response._data !== null) {
				try {
					await saveCachedGet({
						...cacheIdentity,
						url: request,
						query: opts.query,
						headers: {},
						response: res.response._data
					});
				} catch (error) {
					console.error('Failed to cache GET response:', error);
				}
			}

			if (!customOpts?.isSilent) baseStore.isLoading = false;
			CustomOptsUseage(customOpts, TypeEvents.onResponse);
			await customOnResponse?.(res);
		},
		onRequestError: async ({ error }) => {
			if (!customOpts?.isSilent) baseStore.isLoading = false;
			CustomOptsUseage(customOpts, TypeEvents.onRequestError);
			requestFailedBeforeResponse = error.name !== 'AbortError';

			if (
				process.client &&
				isQueueableRequest &&
				!customOpts?.skipOfflineQueue &&
				requestFailedBeforeResponse
			) {
				offlineRequestId = await enqueueOfflineRequest(request, opts);
				requestQueued = true;
				useNotificationStore().addNewAlert(
					'Не удалось отправить запрос. Изменение сохранено и будет отправлено при восстановлении сети.',
					5000,
					AlertsTypes.WARNING
				);
			} else {
				console.error('Request failed before receiving a server response:', error);
			}
		},
		onResponseError: async (error) => {
			if (!customOpts?.isSilent) baseStore.isLoading = false;
			CustomOptsUseage(customOpts, TypeEvents.onResponseError);

			if (error.response?.status === 401 && !customOpts?.isDisabledLogoutAction && !customOpts?.isSilent) {
				const user = useProfileStore();

				await user.logoutHandler(ERRORS_TRANSLATE[error.response?.status] || '');

				return;
			}

			if (customOpts?.isDisabledErrroMessage) return;

			const notification = useNotificationStore();

			const error_key = error.response?.statusText.replaceAll(' ', '_').toLocaleLowerCase();

			let errorFromBack = null;

			let countLetter = 0;

			const dataLength =
				error.response?._data?.msg?.length || error.response?._data?.message?.length || error.response?._data?.length;
			const msg = error.response?._data?.msg || error.response?._data?.message || error.response?._data;
			let reverIndex = dataLength;
			for (let index = 0; index < dataLength; index++) {
				if (countLetter > 3) {
					errorFromBack = msg;
					break;
				}
				if (/[а-яА-ЯЁё]/.test(msg?.[index])) {
					countLetter++;
				}
				reverIndex = reverIndex - 1 > 0 ? reverIndex - 1 : null;
				if (reverIndex !== null && /[а-яА-ЯЁё]/.test(msg?.[reverIndex])) {
					countLetter++;
				}
				if (index > 5) break;
			}

			notification.addNewAlert(
				errorFromBack ||
					ERRORS_TRANSLATE[error_key] ||
					ERRORS_TRANSLATE[error.response?.status] ||
					ERRORS_TRANSLATE.DEFAULT,
				TIMEDEAD_DEFAULT_FOR_ALERT
			);
		},
		...fetchOptions,
		$fetch: useNuxtApp().$api as typeof $fetch
	});

	if (requestQueued) {
		result.error.value = undefined;
		result.status.value = 'pending';
	}

	if (isGetRequest && requestFailedBeforeResponse && cachedGet && result.status.value === 'error') {
		return cachedResponse();
	}

	return Object.assign(result, { offlineRequestId });
};

enum TypeEvents {
	onRequest = 'onRequest',
	onResponse = 'onResponse',
	onRequestError = 'onRequestError',
	onResponseError = 'onResponseError'
}

type CustomOpts = {
	setLoading?: (load: boolean) => void;
	isDisabledErrroMessage?: boolean;
	isDisabledLogoutAction?: boolean;
	isDisabledShowMsgSuccess?: boolean;
	skipOfflineQueue?: boolean;
	isSilent?: boolean;
	isNeedOffline?: boolean;
};

const CustomOptsUseage = (customOpts?: CustomOpts, type?: keyof typeof TypeEvents) => {
	switch (true) {
		case !!customOpts?.setLoading:
			if (type === TypeEvents.onRequest) {
				customOpts.setLoading(true);
			} else {
				customOpts.setLoading(false);
			}

		default:
			break;
	}
};
